import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import React, { useState, useEffect } from 'react';
import { ApiErrorResponse } from "../../../types/ApiErrorResponse";
import { TemplateSelectDto } from "../../admin/templates/types";

import { useTranslation } from "react-i18next";
import { getTemplatesSelect } from "../../admin/templates/api/templateAPI";
import { useNavigate } from 'react-router-dom'
import { SurveyImportCreateDto, SurveyImportDto } from "../types";
import { useQueryClient } from "@tanstack/react-query";
import { surveyImportUpload } from "../api/surveyImportAPI";

export default function SurveyImportForm() {
    const { t } = useTranslation();

    const navigate = useNavigate();
    const [description, setDescription] = useState<string>('');
    const [idTemplate, setTemplateId] = useState<number>(0);
    const [file, setFile] = useState<File>();
    const [templates, setTemplates] = useState<TemplateSelectDto[]>([]);


    const [errors, setErrors] = useState<{ description?: string, file?: string, idTemplate?: string, api?: ApiErrorResponse }>({});
    const [loading, setLoading] = useState(false);
    const queryClient = useQueryClient();

    useEffect(() => {
        const loadTemplates = async () => {
            try {
                setLoading(true);
                const data = await getTemplatesSelect();
                setTemplates(data);
                
                if (data.length > 0) {
                    setTemplateId(data[0].id)
                }
            } catch (error) {
                const newErrors: { description?: string, file?: string, idTemplate?: string, api?: ApiErrorResponse } = {};
                const fetchError = error as ApiErrorResponse;
                newErrors.api = { code: fetchError.code ?? "0", message: fetchError.message }
                setErrors(newErrors);                
            } finally {
                setLoading(false);
            }
        };

        loadTemplates();
    }, []);

    const handleChangeSelect = (e: any) => {
        const id = Number(e.target.value);
        setTemplateId(id);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        const newErrors: { description?: string, file?: string, idTemplate?: string, api?: ApiErrorResponse } = {};

        if (!description.trim()) {
            newErrors.description = t("common:form.requiredFieldForm");
        }

        if (!file) {
            newErrors.file = t("common:form.requiredFieldForm");
        }

        if (!idTemplate) {
            newErrors.idTemplate = t("common:form.requiredFieldForm");
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setLoading(false);
            return;
        }

        // limpiar errores
        const body: SurveyImportCreateDto = {
            description: description,
            templateId: idTemplate,
            file: file!
        }

        try {
            const surveyload: SurveyImportDto = await surveyImportUpload(body);
            const message = t("survey:load.newSurveyLoadSaved", { numberOfRecords: surveyload.numberOfRecords });
            setLoading(false);
            navigate('/survey-load', { state: { message: message } });
            queryClient.invalidateQueries({
                queryKey: ['getSurveyImports'],
                exact: false,
                refetchType: 'all',
            });

        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            newErrors.description = "";
            newErrors.file = "";
            newErrors.api = fetchError
                ? {
                    code: fetchError.code ?? "0",
                    message: fetchError.message
                }
                : { code: "0", message: t("CommonError") }
            setErrors(newErrors);
            setLoading(false);
        }
    };

    return (
        <>
            {loading && (
                <div className="overlay-spinner">
                    <Spinner animation="border" variant="light" />
                </div>
            )}
            <Row className="mb-2">
                <Col xs={12} md={8}>
                    <h2>{t("survey:load.buttonNewLoad")}</h2>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <Button
                        variant="secondary"
                        className="me-2"
                        onClick={() => navigate('/survey-load')}
                    >
                        {t("common:buttons.buttonCancel")}
                    </Button>
                    <Button disabled={loading} onClick={handleSubmit}>{loading ? t("common:buttons.buttonSaveingProgress") : t("common:buttons.buttonSave")}</Button>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            {errors.api?.message && (
                                <Col xs={12} md={12} style={{ marginTop: '40px' }}>
                                    <div className="alert alert-danger">
                                            {errors.api.message}
                                            <button
                                                type="button"
                                                className="btn-close btn-sm position-absolute top-0 end-0 m-2"
                                                aria-label="Close"
                                                onClick={() => setErrors({})}
                                            ></button>
                                    </div>
                                </Col>
                            )}
                        </Row>
                        <Row>
                            <Col xs={12} md={12}>
                                <Form.Group>
                                    <Form.Label>{t("survey:new.newSurveyLoadInputTextDecription")} </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={description}
                                        isInvalid={!!errors.description}
                                        onChange={(e) => setDescription(e.target.value)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.description}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={12}>
                                <Form.Group>
                                    <Form.Label>{t("survey:new.newSurveyLoadInputTextTemplate")} </Form.Label>
                                    <Form.Select value={idTemplate} onChange={handleChangeSelect} isInvalid={!!errors.idTemplate} >
                                        {templates.map((template) => (
                                            <option value={template.id} >{template.description}</option>
                                        ))}
                                    </Form.Select>
                                    <Form.Control.Feedback type="invalid">
                                        {errors.idTemplate}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} md={12}>
                                <Form.Group>
                                    <Form.Label>{t("survey:new.newSurveyLoadInputTextFile")}</Form.Label>
                                    <Form.Control
                                        isInvalid={!!errors.file}
                                        type="file"
                                        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                        onChange={(e) => {
                                            const target = e.target as HTMLInputElement;
                                            const selectedFile = target.files?.[0];
                                            if (selectedFile) {
                                                setFile(selectedFile);
                                            }
                                        }}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.file}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </>
    );
}

